<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { Search as SearchIcon, Loader2 as LoaderIcon } from '@lucide/vue';
import { router } from '@inertiajs/vue3';

defineOptions({
  name: 'GlobalSearch',
});

const props = defineProps({
  placeholder: {
    type: String,
    default: 'Typ hier om te zoeken binnen WIZZR...'
  }
});

// Reactive state
const searchQuery = ref('');
const searchResults = ref({});
const isLoading = ref(false);
const isDropdownVisible = ref(false);
const searchInputRef = ref(null);
const dropdownRef = ref(null);

// Debounce timer
let debounceTimer = null;

// Computed properties
const hasResults = computed(() => {
  return Object.keys(searchResults.value).length > 0;
});

const shouldShowDropdown = computed(() => {
  return isDropdownVisible.value && (hasResults.value || isLoading.value);
});

// Methods
const handleSearch = () => {
  // Clear existing timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Don't search for queries shorter than 2 characters
  if (searchQuery.value.length < 2) {
    searchResults.value = {};
    isDropdownVisible.value = false;
    return;
  }

  // Set loading state
  isLoading.value = true;
  isDropdownVisible.value = true;

  // Debounce search
  debounceTimer = setTimeout(() => {
    performSearch();
  }, 500);
};

const performSearch = async () => {
  try {
    const response = await fetch(`/global-search?q=${encodeURIComponent(searchQuery.value)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      searchResults.value = data.data;
    } else {
      searchResults.value = {};
    }
  } catch (error) {
    console.error('Search error:', error);
    searchResults.value = {};
  } finally {
    isLoading.value = false;
  }
};

const highlightText = (text, query) => {
  if (!query || !text) return text;

  const regex = new RegExp(`(${query})`, 'gi');
  return text.replace(regex, '<span class="search-highlight">$1</span>');
};

const showMore = (category) => {
  if (searchResults.value[category]?.overview_url) {
    const url = new URL(searchResults.value[category].overview_url, window.location.origin);
    router.visit(url.pathname + url.search);
  }
};

const handleInputFocus = () => {
  if (searchQuery.value.length >= 2) {
    isDropdownVisible.value = true;
  }
};

const handleClickOutside = (event) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target) &&
    searchInputRef.value &&
    !searchInputRef.value.contains(event.target)
  ) {
    isDropdownVisible.value = false;
  }
};

const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    isDropdownVisible.value = false;
    searchInputRef.value?.blur();
  }
};

const updateValue = (event) => {
  searchQuery.value = event.target.value;
  handleSearch();
};

const visitLink = (url) => {
  router.visit(url);
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeydown);
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});
</script>

<template>
  <div class="global-search-container">
    <div class="search-input-wrapper">
      <div class="search-icon">
        <SearchIcon :size="18" />
      </div>
      <input
        ref="searchInputRef"
        id="input-global-search"
        type="text"
        class="search-input form-input"
        :placeholder="placeholder"
        :value="searchQuery"
        @input="updateValue"
        @focus="handleInputFocus"
      />
    </div>

    <!-- Search Results Dropdown -->
    <div
      v-if="shouldShowDropdown"
      ref="dropdownRef"
      class="search-dropdown custom-instant-search"
    >
      <!-- Loading State -->
      <div v-if="isLoading" class="search-loading">
        <LoaderIcon class="animate-spin" :size="20" />
        <span>Zoeken...</span>
      </div>

      <!-- Search Results -->
      <div v-else-if="hasResults" class="search-results">
        <div
          v-for="(category, categoryKey) in searchResults"
          :key="categoryKey"
          class="search-category"
        >
          <div class="category-header">
            <h3 class="category-title">{{ category.label }}</h3>
            <a
              v-if="category.overview_url"
              :href="category.overview_url"
              class="see-all-link"
              @click="showMore(categoryKey)"
            >
              Bekijk alles
            </a>
          </div>

          <ul class="category-results">
            <li
              v-for="(result, index) in category.results.slice(0, 3)"
              :key="index"
              class="result-item"
            >
              <div
                class="result-link"
                v-html="highlightText(result.title, searchQuery)"
                @click="visitLink(result.link)"
              ></div>
            </li>
          </ul>

          <div class="category-divider"></div>
        </div>
      </div>

      <!-- No Results -->
      <div v-else-if="searchQuery.length >= 2" class="search-no-results">
        Geen resultaten gevonden voor "{{ searchQuery }}"
      </div>
    </div>
  </div>
</template>

<style scoped>
.global-search-container {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: var(--spacing-md);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-gray-400);
  pointer-events: none;
  z-index: 1;
}

.search-input {
  padding-left: calc(var(--spacing-lg) + var(--spacing-md));
  padding-right: var(--spacing-lg);
  background-color: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  transition: all var(--transition-fast);
}

.search-input:focus {
  background-color: var(--color-white);
  border-color: var(--wizzr-orange);
  box-shadow: 0 0 0 2px var(--wizzr-orange);
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-top: none;
  border-radius: 0 0 var(--radius-md) var(--radius-md);
  box-shadow: var(--shadow-lg);
  max-height: 400px;
  overflow-y: auto;
  z-index: var(--z-dropdown);
}

.search-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl);
  color: var(--color-gray-500);
}

.search-results {
  padding: var(--spacing-md) 0;
}

.search-category {
  border-bottom: 1px solid var(--color-gray-200);
}

.search-category:last-child .category-divider {
  display: none;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-lg);
  background-color: var(--color-gray-50);
}

.category-title {
  font-family: var(--font-family-bold);
  font-size: 0.875rem;
  color: var(--color-gray-700);
  margin: 0;
}

.see-all-link {
  font-size: 0.75rem;
  color: var(--wizzr-orange);
  text-decoration: none;
  font-weight: 500;
  transition: color var(--transition-fast);
}

.see-all-link:hover {
  color: var(--wizzr-blue);
  text-decoration: underline;
}

.category-results {
  list-style: none;
  margin: 0;
  padding: 0;
}

.result-item {
  border-bottom: 1px solid var(--color-gray-100);
}

.result-item:last-child {
  border-bottom: none;
}

.result-link {
  display: block;
  padding: var(--spacing-sm) var(--spacing-lg);
  color: var(--color-gray-700);
  text-decoration: none;
  font-size: 0.875rem;
  line-height: 1.4;
  transition: background-color var(--transition-fast);
  cursor: pointer;
}

.result-link:hover {
  background-color: var(--color-gray-50);
  color: var(--color-gray-900);
}

:deep(.search-highlight) {
  font-weight: 600;
  color: var(--wizzr-orange);
  text-decoration: underline;
  text-decoration-color: var(--wizzr-orange);
  text-underline-offset: 2px;
}

.category-divider {
  height: 1px;
  background-color: var(--color-gray-200);
  margin: var(--spacing-sm) 0;
}

.search-no-results {
  padding: var(--spacing-xl);
  text-align: center;
  color: var(--color-gray-500);
  font-size: 0.875rem;
}

/* Animations */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>